import React, {useEffect} from "react"
import {formatTextTestIds} from "@next/utils"
import {breakpoints} from "@next/themes"
import {useMediaQuery} from "@mui/material"
import ColourChipComponent from "../../../colourchip"
import {
    ColourChipsList,
    ColourChipCollapse,
    ColourChipsContainer,
    ColourChipViewButtonContainer,
    SpinnerImage,
} from "./components"
import env from "../../../../config/env"
import {
    LAZY_SIZES_CLASSNAME,
    SHOW_INITIAL_COLOURCHIPS,
    COLOURCHIP_UNEXPANDED_HEIGHT,
} from "../../../../config/constants"
import {usePreloadImages} from "../../../../hooks/usePreloadImages"
import {TextModel} from "../../../../models/Text"
import {AdditionalColourchipsLoadingState} from "../../../../models/AdditionalColourchipsLoadingState"
import {ColourChip} from "../../../../models/ProductSummary"

interface ColourChipsProps {
    colourchips?: ColourChip[]
    lazyloadColourchips?: boolean
    colourWayImageUrlBuilder: (val: string, val2?: string) => string
    text: TextModel
    title?: string
    numberOfAllColourways: number
    additionalColourchipsLoading: AdditionalColourchipsLoadingState
    realm: string
    fetchAdditionalColourchipsThunk: () => Promise<any>
    setAdditionalColourchipsLoading: (boolean) => void
    brand: string
    slideIndex: (index: number) => void
}

export interface ImageProps {
    src: string
    className?: string
    "data-src"?: string
}

const RenderColourChip = ({list, lazyloadColourchips, brand, slideIndex}) => {
    const firstItemNumber = list[0].itemNumber
    return (
        <>
            {(list as ColourChip[]).map(
                (
                    {
                        itemNumber,
                        linkUrl,
                        isSelected,
                        colour,
                        colourChipUrl,
                        title,
                        price,
                        currencyCode,
                        altText,
                        department,
                    }: ColourChip,
                    index: number,
                ) => {
                    return (
                        <ColourChipComponent
                            src={
                                lazyloadColourchips
                                    ? `${env.REACT_APP_BLOB_STORAGE_PATH}/images/ps/ph.jpg`
                                    : colourChipUrl
                            }
                            dataSrc={lazyloadColourchips ? colourChipUrl : ""}
                            className={lazyloadColourchips ? LAZY_SIZES_CLASSNAME : ""}
                            key={itemNumber}
                            linkUrl={linkUrl}
                            isSelected={!!isSelected}
                            itemNumber={itemNumber}
                            colour={colour || ""}
                            id={itemNumber}
                            title={title}
                            price={price}
                            currencyCode={currencyCode}
                            altText={altText}
                            department={department}
                            index={index}
                            firstItemNumber={firstItemNumber}
                            brand={brand}
                            slideIndex={slideIndex}
                        />
                    )
                },
            )}
        </>
    )
}

const ViewMoreButton = ({triggerShowMore, siteText, additionalColourchipsLoading, realm, ariaLabelText}) => (
    <ColourChipViewButtonContainer onClick={triggerShowMore}>
        <button
            data-testid={formatTextTestIds(`product_summary_colourchips_view_more_button`)}
            type="button"
            aria-label={ariaLabelText}
            aria-expanded={additionalColourchipsLoading === AdditionalColourchipsLoadingState.Loaded}
        >
            <span>
                {additionalColourchipsLoading === AdditionalColourchipsLoadingState.Loaded
                    ? siteText.buttons.viewLessColourchips
                    : siteText.buttons.viewMoreColourchips}
            </span>
            {additionalColourchipsLoading === AdditionalColourchipsLoadingState.Loading ? (
                <SpinnerImage
                    src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/brand/${realm}/loader.gif`}
                    alt="spinner"
                    data-testid="spinner-image"
                />
            ) : (
                <img
                    src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/chevron${
                        additionalColourchipsLoading === AdditionalColourchipsLoadingState.Loaded ? "-up" : ""
                    }.svg`}
                    alt={siteText.buttons.chevronAlt}
                    data-testid={`chevron${
                        additionalColourchipsLoading === AdditionalColourchipsLoadingState.Loaded ? "-up" : ""
                    }`}
                />
            )}
        </button>
    </ColourChipViewButtonContainer>
)

export const StandardColourChipsComponent = ({
    colourchips,
    lazyloadColourchips,
    colourWayImageUrlBuilder,
    text,
    additionalColourchipsLoading,
    numberOfAllColourways,
    realm,
    fetchAdditionalColourchipsThunk,
    setAdditionalColourchipsLoading,
    title,
    brand,
    slideIndex,
}: ColourChipsProps) => {
    const preloadImages = usePreloadImages()
    const isDesktop = useMediaQuery(`(min-width:${breakpoints.values.lg}px)`)

    useEffect(() => {
        // window.requestIdleCallback(() => {
        if (isDesktop) {
            setAdditionalColourchipsLoading(AdditionalColourchipsLoadingState.Default)
        }
        // })
    }, [isDesktop])

    useEffect(() => {
        // window.requestIdleCallback(() => {
        if (colourchips && additionalColourchipsLoading === AdditionalColourchipsLoadingState.Loaded) {
            preloadImages(
                colourchips.slice(SHOW_INITIAL_COLOURCHIPS).map(chip => colourWayImageUrlBuilder(chip.itemNumber)),
            )
        }
        // })
    }, [additionalColourchipsLoading])

    const showViewMore = numberOfAllColourways > SHOW_INITIAL_COLOURCHIPS

    const {viewMoreColoursAria, viewLessColoursAria, product} = text.buttons
    const viewMoreAriaLabel = `${viewMoreColoursAria} ${title || product}`
    const viewLessAriaLabel = `${viewLessColoursAria} ${title || product}`

    const triggerShowMore = async () => {
        if (additionalColourchipsLoading === AdditionalColourchipsLoadingState.Default) {
            await fetchAdditionalColourchipsThunk()
        }
        if (additionalColourchipsLoading === AdditionalColourchipsLoadingState.Loaded) {
            setAdditionalColourchipsLoading(AdditionalColourchipsLoadingState.Default)
        }
    }

    if (!colourchips || colourchips.length <= 1) return null

    return (
        <ColourChipsContainer>
            <ColourChipCollapse
                in={additionalColourchipsLoading === AdditionalColourchipsLoadingState.Loaded}
                data-testid={formatTextTestIds(`product_summary_colourchips_collapse_wrapper`)}
                collapsedSize={
                    colourchips?.length <= 9 ? COLOURCHIP_UNEXPANDED_HEIGHT / 2 : COLOURCHIP_UNEXPANDED_HEIGHT
                }
                timeout={(numberOfAllColourways / 9 + 1) * 100}
            >
                <ColourChipsList data-testid={formatTextTestIds(`product_summary_colourchips`)}>
                    <RenderColourChip
                        list={colourchips}
                        lazyloadColourchips={lazyloadColourchips}
                        brand={brand}
                        slideIndex={slideIndex}
                    />
                </ColourChipsList>
            </ColourChipCollapse>
            {showViewMore && (
                <ViewMoreButton
                    triggerShowMore={triggerShowMore}
                    siteText={text}
                    additionalColourchipsLoading={additionalColourchipsLoading}
                    realm={realm}
                    ariaLabelText={
                        additionalColourchipsLoading === AdditionalColourchipsLoadingState.Loaded
                            ? viewLessAriaLabel
                            : viewMoreAriaLabel
                    }
                />
            )}
        </ColourChipsContainer>
    )
}

export default StandardColourChipsComponent
